import React from "react";
import { Box, Button, ButtonBase, createStyles, Grid, Hidden, makeStyles, Typography, useTheme, useMediaQuery } from "@material-ui/core";
import { IBookingCreateInput, IProtectionPlan, IVehicleGroupProtectionPlan, ProtectionPlanItem } from "../../../../reducers/bookings/types";
import { useSelector } from "react-redux";
import { IAppState } from "../../../../store";
import { useEffect, useState } from "react";
import { getAddonIcon, toCurrency } from "../../../utils";
import { useTranslation } from "react-i18next";

  const useStyles = makeStyles(() =>
    createStyles({
      form: {
        flexGrow: 1,
        maxWidth: "100%"
      },
      addonIcon: {
        width: "100%",
        height: "75px"
      },
      descriptionScroll: {
        "&::-webkit-scrollbar": {
          width: "6px"
        },
        "&::-webkit-scrollbar-track": {
          background: "#f1f1f1",
          borderRadius: "10px"
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#888",
          borderRadius: "10px",
          "&:hover": {
            background: "#555"
          }
        }
      },
      protectionPlanTitle: {
        fontWeight: 700,
        fontSize: "22px",
        lineHeight: 1.2,
        marginBottom: "16px",
        color: "#000000",
        textTransform: "uppercase",
        overflow: "hidden",
        wordBreak: "break-word",
        display: "-webkit-box",
        
      }
    })
  );
  
  interface IProtectionPlanCardProps {
    bookingData: IBookingCreateInput;
    selectedProtectionVehicleGroupPlan: IVehicleGroupProtectionPlan[];
    selectedProtectionPlan?: string;
    onSelect(ProtectionPlan?: string): void;
  }
  export const ProtectionPlanCard: React.FC<IProtectionPlanCardProps> = (
    props
  ) => {
    const { selectedProtectionVehicleGroupPlan } = props;
    const classes = useStyles();
    const theme = useTheme();
    const {t} = useTranslation();
    
    const userState = useSelector((state: IAppState) => state.authReducer.user);
    const [selected, setSelected] = useState<boolean>(
      !!props.selectedProtectionPlan
    );
    const [values, setValues] = useState<Partial<IProtectionPlan>>({
      name: "",
      id: ""
    });            
  
    useEffect(() => {
      if (props.selectedProtectionPlan) {
        const ProtectionPlan = props.selectedProtectionVehicleGroupPlan.find(
          (protectionPlan) =>
            protectionPlan.plan.id === props.selectedProtectionPlan
        )?.plan;
        if (ProtectionPlan) {
          setValues({ ...ProtectionPlan });
        }
        setSelected(true);
      } else {
        setValues({ name: "", id: "" });
        setSelected(false);
      }
    }, [props.selectedProtectionPlan]);
  
    useEffect(() => {
      if (!selected && props.selectedProtectionPlan) {
        props.onSelect();
      }
    }, [selected]);
  
    const uniqueProtectionVehicleGroupPlan = Array.from(
      new Set(
        selectedProtectionVehicleGroupPlan.map(
          (protectionPlan) => protectionPlan.plan.id
        )
      )
    ).map((id) => {
      const protectionPlan = selectedProtectionVehicleGroupPlan.find(
        (protectionPlan) => protectionPlan.plan.id === id
      );
      if (protectionPlan) {
        let rate = 0;
        let rateType = "";
        if (protectionPlan.hasFixedRate) {
          rate = protectionPlan.fixedRate!;
          rateType = "fixed";
        } else if (protectionPlan.recurringRate) {
          const applicableRate = protectionPlan.recurringRate.find(
            (rateItem) =>
              rateItem.rateTypeName === props.bookingData.rateTypeName
          );
          rate = applicableRate?.rate || 0;
          rateType = props.bookingData.rateTypeName;
        }
        return {
          protectionPlan: protectionPlan,
          rate,
          rateType
        };
      }
      return null;
    }).filter((plan): plan is ProtectionPlanItem => plan !== null && plan.rate > 0);
  
    return (
      <Grid container item xs={12}>
        <Hidden smDown>
          <Grid container item xs={1} alignItems="center">
            <img
              className={classes.addonIcon}
              src={getAddonIcon("insurance")}
              alt=""
            ></img>
          </Grid>
        </Hidden>
        <Grid item xs={11}>
          <Grid item xs={12} style={{ marginBottom: 16, }}>
            <Typography variant="h2" style={{ marginLeft: 20, fontWeight: 600}}>
              {t("root:protectionPlans")}
            </Typography>
          </Grid>
          <Grid
              container
              spacing={2}
              justify="flex-start"
              alignItems="stretch"
              style={{ width: "100%", marginLeft: 10 }}
            >
            {uniqueProtectionVehicleGroupPlan.map((singleProtectionPlan) => {
              if (singleProtectionPlan)
                return (
                    <SinglePlan
                      protectionPlanItem={singleProtectionPlan}
                      isSelected={
                        values.id === singleProtectionPlan.protectionPlan.plan.id
                      }
                      hasAnySelected={!!values.id}
                      onClick={() => {
                        if(values.id === singleProtectionPlan.protectionPlan.plan.id){
                          setSelected(false);
                          props.onSelect();
                          setValues({
                            ...values,
                            id: ""
                          });
                          return;
                        }
                        props.onSelect(
                          singleProtectionPlan.protectionPlan.plan.id
                        );
                        setValues({
                          ...values,
                          id: singleProtectionPlan.protectionPlan.plan.id
                        });
                      }}
                      
                    />
                );
            })}
          </Grid>
        </Grid>
      </Grid>
    );
  };
  
  interface ISinglePlanProps {
    protectionPlanItem: ProtectionPlanItem;
    isSelected: Boolean;
    hasAnySelected: Boolean;
    onClick: any;
  }
  const SinglePlan: React.FC<ISinglePlanProps> = (props) => {
    const website = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite);     
    const { locale, currency } = website.organisation;
    const { protectionPlanItem, isSelected, hasAnySelected } = props;
    const {t} = useTranslation();
    const classes = useStyles();
    
    const getHumanizedRate = (rateType: string): string => {
      const rateMap: Record<string, string> = {
        hourly: t("root:hour"),
        daily: t("root:day"),
        weekly: t("root:week"),
        monthly: t("root:month"),
        fixed: ""
      };
      return rateMap[rateType] || "";
    };

    const planExcess = protectionPlanItem?.protectionPlan?.plan?.excess;
    const planFeatures = protectionPlanItem?.protectionPlan?.plan?.features || [];
    const planDescription = protectionPlanItem?.protectionPlan?.plan?.description;
    
    // Apply blur to non-selected cards when any card is selected
    const shouldBlur = hasAnySelected && !isSelected;
    
    return (
      <Grid item xs={12} sm={6} md={4} style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box
          component="div"
          onClick={props.onClick}
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            height: "480px",
            backgroundColor: "#ffffff",
            borderRadius: "10px",
            border: isSelected ? "2px solid #000000" : "1px solid #e0e0e0",
            overflow: "hidden",
            cursor: "pointer",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            transform: isSelected ? "translateY(-8px) scale(1.02)" : "translateY(0) scale(1)",
            boxShadow: isSelected 
              ? "0 12px 24px rgba(0, 0, 0, 0.15), 0 4px 8px rgba(0, 0, 0, 0.1)" 
              : "0 2px 4px rgba(0, 0, 0, 0.05)",
            zIndex: isSelected ? 10 : 1,
            filter: shouldBlur ? "blur(4px)" : "blur(0px)",
            opacity: shouldBlur ? 0.6 : 1,
            position: "relative"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-8px) scale(1.02)";
            e.currentTarget.style.boxShadow = "0 12px 24px rgba(0, 0, 0, 0.15), 0 4px 8px rgba(0, 0, 0, 0.1)";
            e.currentTarget.style.zIndex = "10";
            e.currentTarget.style.filter = "blur(0px)";
            e.currentTarget.style.opacity = "1";
          }}
          onMouseLeave={(e) => {
            if (isSelected) {
              e.currentTarget.style.transform = "translateY(-8px) scale(1.02)";
              e.currentTarget.style.boxShadow = "0 12px 24px rgba(0, 0, 0, 0.15), 0 4px 8px rgba(0, 0, 0, 0.1)";
            } else {
              e.currentTarget.style.transform = "translateY(0) scale(1)";
              e.currentTarget.style.boxShadow = shouldBlur ? "0 2px 4px rgba(0, 0, 0, 0.05)" : "0 2px 4px rgba(0, 0, 0, 0.05)";
              e.currentTarget.style.filter = shouldBlur ? "blur(4px)" : "blur(0px)";
              e.currentTarget.style.opacity = shouldBlur ? "0.6" : "1";
            }
            e.currentTarget.style.zIndex = isSelected ? "10" : "1";
          }}
        >
          <Box
            style={{
              backgroundColor: isSelected ? "#e3f2fd" : "#ffffff",
              padding: "20px 16px",
              borderBottom: "1px solid #f0f0f0",
              flexShrink: 0
            }}
          >
            <Typography variant="h5" className={classes.protectionPlanTitle}>
              {protectionPlanItem.protectionPlan.plan.name}
            </Typography>
            <Box style={{ display: "flex", alignItems: "baseline", gap: "4px" }}>
              <Typography
                style={{
                  fontSize: "28px",
                  fontWeight: 700,
                  color: "#000000",
                  lineHeight: 1
                }}
              >
                {toCurrency(protectionPlanItem.rate, currency, locale)}
              </Typography>
              {protectionPlanItem.rateType !== "fixed" && (
                <Typography
                  style={{
                    fontSize: "14px",
                    color: "#000000",
                    fontWeight: 400
                  }}
                >
                  / {getHumanizedRate(protectionPlanItem.rateType)}
                </Typography>
              )}
            </Box>
          {typeof planExcess === "number" && (
            <Typography
              style={{
                fontSize: "14px",
                color: "#666666",
                lineHeight: 1.6,
                marginTop: "8px"
              }}
            >
              <span style={{ fontWeight: 900, color: "#000000", fontSize: "16px" }}>
                {t("root:excess", "Excess")}
              </span>{" "}
              -{" "}
              {toCurrency(planExcess, currency, locale)}
            </Typography>
          )}
          </Box>

          <Box
            style={{
              padding: "16px",
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: "180px",
              overflowY: "auto",
              overflowX: "hidden"
            }}
            className={classes.descriptionScroll}
          >
            {planFeatures.length > 0 && (
              <Box style={{ marginBottom: planDescription ? "12px" : 0 }}>
                <Typography style={{ fontSize: "14px", color: "#666666", lineHeight: 1.6, marginBottom: "8px" }}>
                  <span style={{ fontWeight: 900, color: "#000000", fontSize: "16px" }}>
                    {(t as any)("root:features")}
                  </span>
                </Typography>
                {planFeatures.map((feature, index) => (
                  <Typography
                    key={index}
                    style={{
                      fontSize: "14px",
                      color: "#666666",
                      lineHeight: 1.6,
                      marginBottom: "4px",
                      display: "flex",
                      alignItems: "flex-start"
                    }}
                  >
                    <span style={{ marginRight: "8px", color: "#000000" }}>•</span>
                    <span style={{ wordBreak: "break-word" }}>{feature}</span>
                  </Typography>
                ))}
              </Box>
            )}
            {planDescription && (
              <Box>
                <Typography style={{ fontSize: "14px", color: "#666666", lineHeight: 1.6, marginTop: planFeatures.length > 0 ? "12px" : 0, marginBottom: "8px" }}>
                  <span style={{ fontWeight: 900, color: "#000000", fontSize: "16px" }}>
                    {(t as any)("root:descriptionLabel")}
                  </span>
                </Typography>
                <Typography style={{ wordBreak: "break-word", fontSize: "14px", color: "#666666", lineHeight: 1.6 }}>
                  {planDescription}
                </Typography>
              </Box>
            )}
          </Box>

          <Box
            style={{
              padding: "16px",
              borderTop: "1px solid #f0f0f0",
              flexShrink: 0
            }}
          >
            <Button
              fullWidth
              variant="contained"
              style={{
                backgroundColor: isSelected ? "#424242" : "#616161",
                color: "#ffffff",
                borderRadius: "6px",
                padding: "10px 20px",
                textTransform: "none",
                fontSize: "14px",
                fontWeight: 500
              }}
              onClick={(e) => {
                e.stopPropagation();
                props.onClick();
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = isSelected ? "#212121" : "#424242";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = isSelected ? "#424242" : "#616161";
              }}
            >
              {isSelected ? t("root:selected") : t("misc:select")}
            </Button>
          </Box>
        </Box>
      </Grid>
    );
  };
  